import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Music2, Check } from "lucide-react";
import type { MusicProviderType } from "@/types/music-provider";

interface ProviderSelectorProps {
  selectedProvider: MusicProviderType;
  isAuthenticated: boolean;
  isConnecting: boolean;
  onProviderChange: (provider: MusicProviderType) => void;
  onConnect: () => void;
  onDisconnect: () => void;
}

const providers = [
  { 
    type: 'spotify' as const, 
    name: 'Spotify', 
    color: 'bg-green-500',
    description: 'Melhor integração e recursos'
  },
  { 
    type: 'apple' as const, 
    name: 'Apple Music', 
    color: 'bg-pink-500',
    description: 'Para usuários iOS'
  },
  { 
    type: 'deezer' as const, 
    name: 'Deezer', 
    color: 'bg-orange-500',
    description: 'Alternativa gratuita'
  }
];

export const ProviderSelector = ({
  selectedProvider,
  isAuthenticated,
  isConnecting,
  onProviderChange,
  onConnect,
  onDisconnect
}: ProviderSelectorProps) => {
  return (
    <Card className="bg-card/95 backdrop-blur-md border-border/50">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Music2 className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">Serviço de Música</h3>
          {isAuthenticated && (
            <Badge variant="outline" className="ml-auto bg-green-500/10 text-green-500 border-green-500/20">
              <Check className="w-3 h-3 mr-1" />
              Conectado
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2">
          {providers.map((provider) => (
            <button
              key={provider.type}
              onClick={() => onProviderChange(provider.type)}
              disabled={isConnecting}
              className={`
                p-3 rounded-lg border-2 transition-all
                ${selectedProvider === provider.type 
                  ? 'border-primary bg-primary/10' 
                  : 'border-border/50 hover:border-border'
                }
                ${isConnecting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              <div className={`w-8 h-8 ${provider.color} rounded-full mx-auto mb-2`} />
              <p className="text-xs font-medium text-center">{provider.name}</p>
            </button>
          ))}
        </div>

        <p className="text-xs text-muted-foreground text-center">
          {providers.find(p => p.type === selectedProvider)?.description}
        </p>

        {isAuthenticated ? (
          <Button
            variant="outline"
            className="w-full"
            onClick={onDisconnect}
            disabled={isConnecting}
          >
            Desconectar
          </Button>
        ) : (
          <Button
            className="w-full bg-gradient-to-br from-primary to-accent"
            onClick={onConnect}
            disabled={isConnecting}
          >
            {isConnecting ? 'Conectando...' : 'Conectar'}
          </Button>
        )}

        {selectedProvider === 'spotify' && !isAuthenticated && (
          <p className="text-xs text-muted-foreground">
            💎 Requer Spotify Premium para reprodução completa
          </p>
        )}
      </CardContent>
    </Card>
  );
};
